<section class="bluesage-push-panel p-p-3" *ngIf="viewModel$ | async as viewModel">
    <ng-container *ngIf="viewModel.active">

        <!-- Before anything reaches BlueSage. `transactions.create` writes the
             node, recomputes the job totals and grants access, and on a loaded
             instance that has taken twenty seconds — with nothing on screen but
             a spinner inside the button. An operator who sees no panel assumes
             the click was lost and presses Send again. -->
        <div *ngIf="viewModel.phase === 'preparing'">
            <div class="p-d-flex p-ai-center p-jc-between">
                <span>
                    <i class="pi pi-spin pi-spinner p-mr-2"></i>
                    Recording {{ viewModel.amount | currency }} on the job —
                    nothing has been sent to {{ viewModel.device }} yet.
                </span>
                <small class="p-text-secondary">{{ viewModel.elapsedSeconds }}s</small>
            </div>

            <p class="p-text-secondary p-mt-2 p-mb-0">
                The terminal is asked once this is saved. Nothing is on the
                reader, and the customer has not been prompted.
            </p>
        </div>

        <!-- ⚠ This phase covers the WHOLE customer interaction, not just our
             latency, and that is a property of the contract rather than a choice.
             `POST /v1/terminal-pushes` long-polls while the cardholder reads the
             screen and only returns 202 with a `push_id` once they are done — so
             the CRM has nothing to cancel WITH during the one window where
             cancelling matters. Raised as middleware ask 9.

             Saying "sending" alone for up to five minutes would read as a hung
             screen, so it says what is actually happening and what the operator
             can still do: cancel on the device itself. -->
        <div *ngIf="viewModel.phase === 'sending'">
            <div class="p-d-flex p-ai-center p-jc-between">
                <span>
                    <i class="pi pi-spin pi-spinner p-mr-2"></i>
                    Waiting for the customer at {{ viewModel.device }} —
                    {{ viewModel.amount | currency }}
                </span>
                <small class="p-text-secondary">{{ viewModel.elapsedSeconds }}s</small>
            </div>

            <p class="p-text-secondary p-mt-2 p-mb-0">
                The amount is on the terminal now. To stop it, press <strong>cancel
                on the device</strong> — the result will show here either way.
            </p>
        </div>

        <!-- On the device. This is the customer's time, and it is counted. -->
        <div *ngIf="viewModel.phase === 'waiting'">
            <div class="p-d-flex p-ai-center p-jc-between">
                <span>
                    <i class="pi pi-spin pi-spinner p-mr-2"></i>
                    On the terminal — waiting for the customer at {{ viewModel.device }}
                </span>
                <small class="p-text-secondary">{{ viewModel.elapsedSeconds }}s</small>
            </div>
            <p class="p-text-secondary p-mt-2 p-mb-1">
                The customer is being asked to tap and tip. This can take a few
                minutes on a slow connection. The result shows here the moment
                the device answers — leave this open.
            </p>

            <!-- Requirement: the completion tip screen gives roughly 30 seconds and
                 is NOT settable. Showing the countdown lets the operator coach the
                 customer rather than guess how long is left. -->
            <p class="p-mt-2 p-mb-1" *ngIf="viewModel.tipSecondsLeft !== undefined">
                <strong>{{ viewModel.tipSecondsLeft }}s</strong> left on the tip prompt.
                <span class="p-text-secondary">
                    The customer needs to answer it on the terminal.
                </span>
            </p>

            <p-progressBar
                class="p-mt-2"
                [value]="viewModel.ceilingProgress"
                [showValue]="false"
                styleClass="p-mt-2"
                [style]="{ height: '4px' }"
            ></p-progressBar>

            <p-button
                class="p-mt-2"
                label="Cancel"
                severity="secondary"
                size="small"
                [outlined]="true"
                [loading]="viewModel.cancelling"
                *ngIf="viewModel.showCancel"
                (onClick)="store.dispatch(actions.cancelButtonClicked())"
            ></p-button>
        </div>

        <!-- Money taken. Says what the customer paid AND what the job is
             credited, because on a dual-pricing location they differ and the
             number the customer quotes down the phone must appear somewhere. -->
        <div *ngIf="viewModel.phase === 'settled'" class="p-d-flex p-ai-center">
            <i class="pi pi-check-circle p-mr-2" style="color: var(--green-500)"></i>
            <span>Paid — the customer paid {{ viewModel.amount | currency }} at {{ viewModel.device }}.</span>
        </div>

        <!-- Requirement: an authorization is money HELD, never taken. Saying
             "Paid" here would put money on the job the merchant has not
             received and cannot spend. -->
        <div *ngIf="viewModel.phase === 'authorized'">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-lock p-mr-2" style="color: var(--primary-color)"></i>
                <span>
                    Authorized — {{ viewModel.amount | currency }} is being HELD on the
                    customer's card. It has not been taken yet.
                </span>
            </div>
            <!-- ⚠ An open authorization holds the cardholder's funds until the
                 issuer expires it, and there is no way to release one once the
                 originating terminal no longer holds it. Completing or
                 cancelling has to read as the obvious next step. -->
            <p class="p-mt-2 p-text-secondary">
                Complete it when the job is priced, or cancel it — an authorization
                left open keeps the customer's money frozen.
            </p>
        </div>

        <div *ngIf="viewModel.phase === 'declined'">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-times-circle p-mr-2" style="color: var(--red-500)"></i>
                <span>{{ viewModel.message || 'The card was declined. Nothing was charged.' }}</span>
            </div>
        </div>

        <!-- A cancelled COMPLETION is the one cancel with a next step. The device
             answers a ticket "Time out" when it does not pick the push up
             (2026-09-21: six crew completions in one afternoon), the hold is
             untouched, and Complete can simply be pressed again — the server
             sends a new push. Without saying so, crews took fresh sales instead
             and left the holds open on the customers' cards. -->
        <div *ngIf="viewModel.phase === 'cancelled'">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-ban p-mr-2 p-text-secondary"></i>
                <span *ngIf="viewModel.kind !== 'ticket'">Cancelled — nothing was charged.</span>
                <span *ngIf="viewModel.kind === 'ticket'">Cancelled — nothing was captured. The hold is still open.</span>
            </div>
            <p *ngIf="viewModel.kind === 'ticket'" class="p-text-secondary p-mt-2 p-mb-0">
                The terminal did not take the completion. Make sure it is awake and on its
                home screen, then press <strong>Complete pre-auth</strong> again — do not
                take a new payment, or the customer is charged on top of the hold.
            </p>
        </div>

        <!-- Distinct from declined: a timeout means the device never answered
             within the middleware's five minutes, so nothing is KNOWN. Live on
             2026-09-03 the customer completed on the device AFTER that window
             — twice — and the money surfaced in the review queue. Telling the
             operator exactly that, and where to look, is the only honest
             instruction; "retry" is how a customer gets charged twice. -->
        <div *ngIf="viewModel.phase === 'timedOut'">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-clock p-mr-2" style="color: var(--orange-500)"></i>
                <span>
                    The terminal did not answer within 5 minutes.
                    <strong>Nothing is confirmed.</strong>
                </span>
            </div>
            <p class="p-text-secondary p-mt-2 p-mb-0">
                If the customer completed on the device, the payment arrives in
                <strong>Unmatched payments</strong> within a few minutes and can be
                matched to this job from there. Check the device and that page
                before sending again.
            </p>
        </div>

        <!-- The CRM lost sight of the push — it has been in flight longer than
             any request behind it could have lived. Same instruction as a
             timeout, because the same thing is true: nothing is known. -->
        <div *ngIf="viewModel.phase === 'lost'">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-question-circle p-mr-2" style="color: var(--orange-500)"></i>
                <span>We lost track of this push. <strong>Nothing is confirmed.</strong></span>
            </div>
            <p class="p-text-secondary p-mt-2 p-mb-0">
                Check the device, and check <strong>Unmatched payments</strong> for
                the money before sending again.
            </p>
        </div>

        <div *ngIf="viewModel.phase === 'failed'" class="p-d-flex p-ai-center">
            <i class="pi pi-exclamation-circle p-mr-2" style="color: var(--red-500)"></i>
            <span>{{ viewModel.message || 'That could not be sent to the terminal.' }}</span>
        </div>

        <!-- A cancel that raced the device leaves a note without changing the
             outcome — `push_not_cancellable` means the terminal already
             finished, not that anything failed. -->
        <p class="p-text-secondary p-mt-2" *ngIf="viewModel.message && viewModel.phase === 'waiting'">
            {{ viewModel.message }}
        </p>

        <!-- The push never left, so the transaction written to push against was
             cancelled. Said plainly, because a row silently appearing and then
             silently closing is worse than either. -->
        <p class="p-text-secondary p-mt-2 p-mb-0" *ngIf="viewModel.transactionDiscarded">
            Nothing was sent and nothing was charged, so the draft payment has
            been cancelled on the job.
        </p>

        <div class="bluesage-push-actions p-mt-2">
            <!-- Requirement: every finished push has a way out of the panel.
                 The dialog swaps its whole form for this panel, so with no
                 control here a settled or cancelled push left a screen nobody
                 could leave except by reloading the page. -->
            <p-button
                label="Done"
                severity="secondary"
                size="small"
                [outlined]="true"
                *ngIf="viewModel.finished"
                (onClick)="store.dispatch(actions.doneButtonClicked({ phase: viewModel.phase }))"
            ></p-button>

            <!-- Where the money surfaces if the customer completed on the device
                 after the panel lost sight of the push. -->
            <p-button
                label="Open Unmatched payments"
                icon="pi pi-external-link"
                size="small"
                severity="warning"
                [outlined]="true"
                *ngIf="viewModel.showUnmatchedLink"
                (onClick)="store.dispatch(actions.openUnmatchedPaymentsClicked())"
            ></p-button>

            <!-- Safe by construction: the idempotency key is derived from the
                 transaction id and push type, so this REPLAYS the original push
                 rather than queueing a second prompt, and never creates a second
                 transaction. -->
            <p-button
                label="Try again"
                size="small"
                *ngIf="viewModel.showTryAgain"
                (onClick)="store.dispatch(actions.tryAgainButtonClicked())"
            ></p-button>
        </div>
    </ng-container>
</section>
